<template>
  <!-- XOL-596 (readable read-only remarks; Carlos, 2026-10-14: "Remarks for step 4 are
       getting lost when the step goes to completed. Make the remarks easier to read in all
       steps when the approvals are locked or the step is marked as completed."):
       one framed, labelled block for a remarks field that can no longer be edited (the
       approval is locked, the step is completed, or the viewer never writes it).

       Pattern A (a two-state surface): the step renders `h5.remarks-heading` itself, above
       the editor AND above this block, and passes only `ariaLabel`.
       Pattern B (always read-only): pass `label`; the heading is rendered here and the
       root becomes a `.remarks-section`.

       Presentational: no requests, no buttons, nothing dismissible, never role="alert".
       The body is a <div>, not a <p>: quill HTML is block content (<p>, <ul>, <h1>).
       The root keeps `html-remarks` so `.html-remarks[aria-label="…"]` selectors still match. -->
  <div class="ro-remarks html-remarks" :class="{ 'remarks-section': !!label }"
       role="region" :aria-label="ariaLabel || label">
    <h5 v-if="label" class="remarks-heading">{{label}}</h5>
    <div class="ro-remarks__frame" :style="frameStyle" :tabindex="maxHeight ? 0 : (focusable ? -1 : null)">
      <!-- Distinct tags AND keys: Vue reuses a same-tag v-if/v-else element, and v-safe-html
           writes innerHTML that Vue does not track, so a `<p v-safe-html>` flipping to a
           `<p>No remarks</p>` kept the old text and appended the empty line beneath it (a
           refused remark looked saved, XOL-596). -->
      <div v-if="hasText" key="body" class="ro-remarks__body" v-safe-html="html"></div>
      <p v-else key="empty" class="ro-remarks__empty">{{shownEmptyText}}</p>
    </div>
    <small v-if="stamp" class="remarks-stamp">{{stamp}}</small>
  </div>
</template>

<script>
import { hasRemarksText } from '@/lib/remarksText'
import { copyText } from '@/lib/requestSyncCopy'

export default {

  name: 'ReadOnlyRemarks',

  props: {

    // The stored remarks HTML.
    html: {
      type: String,
      default: null
    },

    // The visible heading. When set, this component renders it (pattern B).
    label: {
      type: String,
      default: null
    },

    // The region's name; defaults to `label`. Pattern A passes only this, because its
    // heading sits above, in the step.
    ariaLabel: {
      type: String,
      default: null
    },

    // Shown when `html` has no text. Falls back to copyText('requestSync.remarks.empty').
    emptyText: {
      type: String,
      default: null
    },

    // The ALREADY formatted lastUpdatedLine(...) string.
    stamp: {
      type: String,
      default: null
    },

    // e.g. '12em': the frame scrolls vertically and becomes keyboard focusable.
    maxHeight: {
      type: String,
      default: null
    },

    // The frame can take focus from a script (tabindex -1, not in the tab order): the review footer's "Show review" moves focus here (decision 190).
    focusable: {
      type: Boolean,
      default: false
    }

  },

  computed: {

    hasText () {
      return hasRemarksText(this.html)
    },

    shownEmptyText () {
      return this.emptyText || copyText('requestSync.remarks.empty')
    },

    frameStyle () {
      return this.maxHeight ? { maxHeight: this.maxHeight, overflowY: 'auto' } : null
    }

  }

}
</script>

<style scoped>
.ro-remarks {
  overflow: visible;
}
.ro-remarks__frame {
  box-sizing: border-box;
  max-width: 720px;
  padding: 12px 16px;
  background: #FAFAFA;
  border: 1px solid #EBEEF5;
  border-radius: 4px;
}
.ro-remarks__frame:focus {
  outline: 2px solid #409EFF;
  outline-offset: 2px;
}
.ro-remarks__body {
  font-size: 14px;
  line-height: 1.6;
  color: #303133;
  overflow-wrap: break-word;
  word-wrap: break-word;
  overflow-x: auto;
}
.ro-remarks__body ::v-deep p {
  margin: 0 0 8px;
}
.ro-remarks__body ::v-deep ul,
.ro-remarks__body ::v-deep ol {
  margin: 0 0 8px;
  padding-left: 24px;
}
.ro-remarks__body ::v-deep li {
  margin: 0 0 2px;
}
.ro-remarks__body ::v-deep h1 {
  font-size: 18px;
}
.ro-remarks__body ::v-deep h2 {
  font-size: 16px;
}
.ro-remarks__body ::v-deep h3,
.ro-remarks__body ::v-deep h4,
.ro-remarks__body ::v-deep h5,
.ro-remarks__body ::v-deep h6 {
  font-size: 15px;
}
.ro-remarks__body ::v-deep h1,
.ro-remarks__body ::v-deep h2,
.ro-remarks__body ::v-deep h3,
.ro-remarks__body ::v-deep h4,
.ro-remarks__body ::v-deep h5,
.ro-remarks__body ::v-deep h6 {
  margin: 12px 0 6px;
  color: #303133;
  font-weight: 600;
  line-height: 1.4;
}
.ro-remarks__body ::v-deep blockquote {
  margin: 0 0 8px;
  padding-left: 10px;
  border-left: 3px solid #DCDFE6;
  color: #606266;
}
.ro-remarks__body ::v-deep pre {
  margin: 0 0 8px;
  padding: 8px 10px;
  background: #F5F7FA;
  border-radius: 4px;
  font-size: 13px;
  white-space: pre-wrap;
  overflow-x: auto;
}
.ro-remarks__body ::v-deep img {
  max-width: 100%;
  height: auto;
}
.ro-remarks__body ::v-deep a {
  color: inherit;
  text-decoration: underline;
}
.ro-remarks__body ::v-deep > :first-child {
  margin-top: 0;
}
.ro-remarks__body ::v-deep > :last-child {
  margin-bottom: 0;
}
.ro-remarks__empty {
  margin: 0;
  font-size: 14px;
  line-height: 1.6;
  color: #606266;
  font-style: italic;
}
@media (max-width: 479px) {
  .ro-remarks__frame {
    padding: 10px 12px;
  }
}
</style>
